import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { TranslateModule } from '@ngx-translate/core';

// Routing
import { AuthRoutingModule } from './auth-routing.module';

// Components
import { LandingComponent } from './components/landing/landing.component';
import { CallbackComponent } from './components/callback/callback.component';
import { TokenExpiryDialogComponent } from './components/token-expiry-dialog/token-expiry-dialog.component';
import { TokenRefreshModalComponent } from './components/token-refresh-modal/token-refresh-modal.component';

// Note: Services (AuthService, TokenStorageService, PlatformService, DeepLinkService,
// MobileTokenRefreshService) use providedIn: 'root' and are automatically singleton.
// They should NOT be added to the providers array to avoid duplicate instances.

// Guards
import { AuthGuard } from './guards/auth.guard';

// Interceptors
import { TokenInterceptor } from './interceptors/token.interceptor';

// OAuth storage: nonce + PKCE verifier shared across tabs (MAF-671)
import { NonceSharingOAuthStorage } from './services/nonce-sharing-oauth-storage';

/**
 * AuthModule
 *
 * Comprehensive authentication module for MyABVV application.
 * Provides OAuth2/OIDC authentication with Belgian FAS integration,
 * platform-aware implementations, and secure token storage.
 *
 * Features:
 * - OAuth2 Authorization Code Flow with PKCE
 * - Belgian FAS custom claims support
 * - Secure token storage (Keychain/KeyStore/sessionStorage)
 * - Mobile deep linking support
 * - Route guards for protected routes
 * - HTTP interceptor for automatic token attachment
 * - Token expiry warning dialog
 * - User-friendly error handling
 * - Test mode for app store reviewers (5-tap activation)
 *
 * Usage:
 * Import this module in AppModule and configure routes.
 */
@NgModule({
    imports: [
        CommonModule,
        RouterModule,
        ReactiveFormsModule,  // Required for test mode login form
        AuthRoutingModule,
        OAuthModule.forRoot(),
        TranslateModule,
        // Standalone Components
        LandingComponent,
        CallbackComponent,
        TokenExpiryDialogComponent,
        TokenRefreshModalComponent
    ],
    providers: [
        // Note: AuthService, TokenStorageService, PlatformService, DeepLinkService,
        // and MobileTokenRefreshService are NOT listed here because they use
        // providedIn: 'root' and should be singletons across the entire app.
        // Listing them here would create duplicate instances when this module
        // is lazy-loaded.

        // Overrides OAuthModule's default (sessionStorage): the itsme same-device flow
        // finishes the login in a new tab that must find the nonce (MAF-667/MAF-671)
        { provide: OAuthStorage, useClass: NonceSharingOAuthStorage },
        // Guards
        AuthGuard,
        // HTTP Interceptors
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        }
    ],
    exports: [
        // Export components that might be used in other modules
        TokenExpiryDialogComponent
    ]
})
export class AuthModule { }
